import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import dayjs from 'dayjs';

const DIAS_MES_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

const SummaryItem = ({ label, value }) => (
  <Grid item xs={12}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={600}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ChipsRow = ({ label, items }) => {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {items.map((item) => (
          <Chip key={item} label={item} color="primary" variant="outlined" />
        ))}
      </Box>
    </Box>
  );
};

const isDiaVacio = (d) => d === '' || d == null;

const hasQuincenalCustomDays = (data) =>
  data.frecuencia === 'Quincenal' &&
  data.diasPagoQuincenal &&
  !isDiaVacio(data.diasPagoQuincenal.dia1) &&
  !isDiaVacio(data.diasPagoQuincenal.dia2) &&
  Number(data.diasPagoQuincenal.dia1) !== Number(data.diasPagoQuincenal.dia2);

/** Fechas de pago quincenal alineadas a dos días fijos del mes (misma lógica que el backend). */
const buildQuincenalPaymentDates = (fechaInicio, dia1, dia2, count) => {
  const a = Math.min(dia1, dia2);
  const b = Math.max(dia1, dia2);
  const start = dayjs(fechaInicio).startOf('day');
  const candidates = [];
  let y = start.year();
  let m = start.month();
  for (let guard = 0; guard < 800 && candidates.length < count + 5; guard++) {
    const dim = dayjs(new Date(y, m, 1)).daysInMonth();
    for (const day of [a, b]) {
      const dd = Math.min(day, dim);
      const dt = dayjs(new Date(y, m, dd)).startOf('day');
      if (dt.isAfter(start) || dt.isSame(start, 'day')) {
        candidates.push(dt);
      }
    }
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return candidates
    .sort((x, y) => x.valueOf() - y.valueOf())
    .slice(0, count);
};

const calculateAmortizationFija = (data) => {
  const { plazo, frecuencia, cuotaEstimada, fechaInicio } = data;
  const cuotas = Number(plazo) || 0;
  if (!cuotas || !fechaInicio || !cuotaEstimada) {
    return [];
  }

  const amortization = [];
  let fecha = dayjs(fechaInicio);
  const useCustomQ = hasQuincenalCustomDays(data);
  const fechasQuinc = useCustomQ
    ? buildQuincenalPaymentDates(
        fechaInicio,
        Number(data.diasPagoQuincenal.dia1),
        Number(data.diasPagoQuincenal.dia2),
        cuotas
      )
    : [];

  for (let i = 0; i < cuotas; i++) {
    if (useCustomQ && fechasQuinc[i]) {
      fecha = fechasQuinc[i];
    } else {
      switch (frecuencia) {
        case 'Diario':
          fecha = fecha.add(1, 'day');
          break;
        case 'Semanal':
          fecha = fecha.add(7, 'day');
          break;
        case 'Quincenal':
          fecha = fecha.add(15, 'day');
          break;
        default:
          fecha = fecha.add(1, 'month');
          break;
      }
    }

    amortization.push({
      numero_cuota: i + 1,
      fecha: fecha.format('DD/MM/YYYY'),
      cuota: Number(cuotaEstimada).toFixed(2),
    });
  }

  return amortization;
};

const calculateAmortizationInsoluto = (data) => {
  const { plazo, frecuencia, montoSolicitado, interes, fechaInicio } = data;
  const cuotas = Number(plazo) || 0;
  const principal = Number(montoSolicitado) || 0;
  const tasaAnual = Number(interes) || 0;

  if (!cuotas || !fechaInicio || !principal || tasaAnual === undefined) {
    return [];
  }

  // Calculate periodic interest rate based on frequency
  let r = 0; // periodic interest rate as decimal
  switch (frecuencia) {
    case 'Semanal':
      r = (tasaAnual / 100) / 4;
      break;
    case 'Quincenal':
      r = (tasaAnual / 100) / 2;
      break;
    case 'Mensual':
      r = (tasaAnual / 100) / 1;
      break;
    case 'Diario':
      r = (tasaAnual / 100) / cuotas;
      break;
    default:
      r = (tasaAnual / 100) / 1; // default to monthly
      break;
  }

  // German method: Fixed capital payment, decreasing interest
  const capitalFijo = principal / cuotas;

  const amortization = [];
  let saldo = principal;
  let fecha = dayjs(fechaInicio);
  const useCustomQ = hasQuincenalCustomDays(data);
  const fechasQuinc = useCustomQ
    ? buildQuincenalPaymentDates(
        fechaInicio,
        Number(data.diasPagoQuincenal.dia1),
        Number(data.diasPagoQuincenal.dia2),
        cuotas
      )
    : [];

  for (let i = 0; i < cuotas; i++) {
    if (useCustomQ && fechasQuinc[i]) {
      fecha = fechasQuinc[i];
    } else {
      switch (frecuencia) {
        case 'Diario':
          fecha = fecha.add(1, 'day');
          break;
        case 'Semanal':
          fecha = fecha.add(7, 'day');
          break;
        case 'Quincenal':
          fecha = fecha.add(15, 'day');
          break;
        default:
          fecha = fecha.add(1, 'month');
          break;
      }
    }

    // German method: Fixed capital, interest on remaining balance
    let capital = capitalFijo;
    const interesPeriodo = saldo * r;
    let cuotaFinal = capital + interesPeriodo;

    // Adjust last payment to pay off remaining balance
    if (i === cuotas - 1) {
      capital = saldo;
      cuotaFinal = capital + interesPeriodo;
    }

    // Round to 2 decimal places
    capital = Math.round(capital * 100) / 100;
    const interesRedondeado = Math.round(interesPeriodo * 100) / 100;
    cuotaFinal = Math.round(cuotaFinal * 100) / 100;

    amortization.push({
      numero_cuota: i + 1,
      fecha: fecha.format('DD/MM/YYYY'),
      cuota: cuotaFinal.toFixed(2),
      interes: interesRedondeado.toFixed(2),
      capital: capital.toFixed(2),
    });

    // Update remaining balance
    saldo = Math.round((saldo - capital) * 100) / 100;
    if (saldo < 0) {
      saldo = 0;
    }
  }


  return amortization;
};

const calculateAmortizationIndefinido = (data) => {
  const { plazo, frecuencia, montoSolicitado, interes, fechaInicio } = data;
  const cuotas = Number(plazo) || 0;
  const principal = Number(montoSolicitado) || 0;
  const tasaAnual = Number(interes) || 0;

  let r = 0;
  switch (frecuencia) {
    case 'Semanal':
      r = (tasaAnual / 100) / 4;
      break;
    case 'Quincenal':
      r = (tasaAnual / 100) / 2;
      break;
    case 'Mensual':
      r = (tasaAnual / 100) / 1;
      break;
    case 'Diario':
      r = (tasaAnual / 100) / 30;
      break;
    default:
      r = (tasaAnual / 100) / 1; // default to monthly
      break;
  }

  const amortization = [];
  let fecha = dayjs(fechaInicio);

  var cuota = Number(principal * r).toFixed(2);
  amortization.push({
    numero_cuota: 1,
    fecha: fecha.format('DD/MM/YYYY'),
    cuota: cuota,
  });




  return amortization;
};

const AmortizationTable = ({ data }) => {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <StyledCellHeader>Número de Cuota</StyledCellHeader>
            <StyledCellHeader>Fecha</StyledCellHeader>
            <StyledCellHeader>Cuota</StyledCellHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.numero_cuota}>
              <TableCell sx={{textAlign:"center"}}>{item.numero_cuota}</TableCell>
              <TableCell>{item.fecha}</TableCell>
              <TableCell>{item.cuota}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const AmortizationDrawer = ({ open, onClose, loanData, onDiasPagoQuincenalChange }) => {
  const data = loanData || {};
  const quincDias = data.diasPagoQuincenal || { dia1: '', dia2: '' };
  const moneda = data.moneda ? `${data.moneda}$` : '';
  const mostrarDiasQuincenal =
    data.frecuencia === 'Quincenal' && data.metodo && data.metodo !== 'plazo_indefinido';
  const dia1Vacio = isDiaVacio(quincDias.dia1);
  const dia2Vacio = isDiaVacio(quincDias.dia2);
  const diasQuincIguales =
    mostrarDiasQuincenal && !dia1Vacio && !dia2Vacio && Number(quincDias.dia1) === Number(quincDias.dia2);
  const diasQuincIncompletos = mostrarDiasQuincenal && dia1Vacio !== dia2Vacio;

  const handleDiaChange = (field, value) => {
    const num = value === '' ? '' : Number(value);
    if (!onDiasPagoQuincenalChange) return;
    if (field === 'dia1') {
      onDiasPagoQuincenalChange({ ...quincDias, dia1: num });
    } else {
      onDiasPagoQuincenalChange({ ...quincDias, dia2: num });
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || Number.isNaN(amount)) {
      return '—';
    }
    return `${moneda}${formatNumberWithCommas(Number(amount).toFixed(2))}`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      hideBackdrop
      ModalProps={{
        keepMounted: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 440 },
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          pointerEvents: 'auto',
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiDrawer-paper': {
          pointerEvents: 'auto',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Parámetros del préstamo
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Revisa los valores de la amortización. Para frecuencia quincenal, define aquí los dos días del mes de pago;
          se aplican al guardar el préstamo. Si los dejas vacíos, las cuotas se calculan cada 15 días a partir de la
          fecha del préstamo.
        </Typography>

        {mostrarDiasQuincenal && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }} color="text.primary">
              Días de pago (quincenal)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={diasQuincIguales || (diasQuincIncompletos && dia1Vacio)}>
                  <InputLabel id="amort-dia-quinc-1" shrink>1.er día</InputLabel>
                  <Select
                    labelId="amort-dia-quinc-1"
                    label="1.er día"
                    notched
                    displayEmpty
                    value={quincDias.dia1}
                    onChange={(e) => handleDiaChange('dia1', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Sin definir</em>
                    </MenuItem>
                    {DIAS_MES_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" error={diasQuincIguales || (diasQuincIncompletos && dia2Vacio)}>
                  <InputLabel id="amort-dia-quinc-2" shrink>2.º día</InputLabel>
                  <Select
                    labelId="amort-dia-quinc-2"
                    label="2.º día"
                    notched
                    displayEmpty
                    value={quincDias.dia2}
                    onChange={(e) => handleDiaChange('dia2', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Sin definir</em>
                    </MenuItem>
                    {DIAS_MES_OPTIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {diasQuincIguales && (
              <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                Seleccione dos días distintos del mes.
              </FormHelperText>
            )}
            {diasQuincIncompletos && (
              <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                Seleccione ambos días o deje los dos vacíos.
              </FormHelperText>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        <Grid container spacing={2}>
          <SummaryItem label="Monto solicitado" value={formatCurrency(data.montoSolicitado)} />
          <SummaryItem label="Cuota estimada" value={formatCurrency(data.cuotaEstimada)} />
          <SummaryItem label="Deuda total" value={formatCurrency(data.deudaTotal)} />
          <SummaryItem label="Plazo" value={data.plazo ? `${data.plazo} cuotas` : '—'} />
          <SummaryItem label="Frecuencia de pago" value={data.frecuencia || '—'} />
          <SummaryItem label="Método" value={data.metodo || '—'} />
          <SummaryItem label="Fecha de inicio" value={data.fechaInicio || '—'} />
          <SummaryItem label="Interés" value={data.interes ? `${data.interes}%` : '—'} />
          <SummaryItem label="Mora" value={data.mora ? `${data.mora}${data.moraEsPorcentaje ? '%' : ''}` : '—'} />
        </Grid>

        <Divider />

        {data.metodo === 'fija' && (
          <AmortizationTable data={calculateAmortizationFija(data)} />
        )}
        {data.metodo === 'plazo_indefinido' && (
          <AmortizationTable data={calculateAmortizationIndefinido(data)} />
        )}
        {data.metodo === 'insoluto' && (
          <AmortizationTable data={calculateAmortizationInsoluto(data)} />
        )}

        {!data.montoSolicitado && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'grey.100',
              border: '1px dashed',
              borderColor: 'grey.300',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Completa al menos el monto solicitado, plazo y frecuencia para visualizar la información completa de la
              amortización.
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AmortizationDrawer;

